/**
 * Service para comunicação com API do módulo Sales
 */

import { apiService } from '@/lib/api';
import { Lead, CreateLeadDTO, KanbanData, Origem, Indicador, Produto, Proposta } from '@/types/sales';
import { FlowConfig } from '@/types/flow';
import { Node, Edge } from 'reactflow';
import { FlowNodeData } from '@/types/flow';

class SalesService {
  // ============================================================================
  // LEADS
  // ============================================================================

  async getLeads(filters?: {
    etapa?: string;
    vendedor_id?: number;
    search?: string;
  }): Promise<Lead[]> {
    const response = await apiService.get('/sales/leads', { params: filters });
    return response.leads;
  }

  async getKanban(filters?: {
    vendedor_id?: number;
    suporte_id?: number;
  }): Promise<KanbanData> {
    const response = await apiService.get('/sales/leads/kanban', { params: filters });
    return response.kanban;
  }

  async getLead(id: number): Promise<Lead> {
    const response = await apiService.get(`/sales/leads/${id}`);
    return response.lead;
  }

  async createLead(data: CreateLeadDTO): Promise<Lead> {
    const response = await apiService.post('/sales/leads', data);
    return response.lead;
  }

  async updateLead(id: number, data: Partial<CreateLeadDTO>): Promise<Lead> {
    const response = await apiService.put(`/sales/leads/${id}`, data);
    return response.lead;
  }

  async moverLeadEtapa(id: number, etapa: string, observacao?: string): Promise<Lead> {
    const response = await apiService.put(`/sales/leads/${id}/etapa`, { etapa, observacao });
    return response.lead;
  }

  async deleteLead(id: number): Promise<void> {
    await apiService.delete(`/sales/leads/${id}`);
  }

  // ============================================================================
  // ORIGENS
  // ============================================================================

  async getOrigens(search?: string): Promise<Origem[]> {
    const params = search ? { search } : {};
    const response = await apiService.get('/sales/origens', { params });
    return response.origens;
  }

  async createOrigem(data: { nome: string }): Promise<Origem> {
    const response = await apiService.post('/sales/origens', data);
    return response.origem;
  }

  async updateOrigem(id: number, data: { nome: string }): Promise<Origem> {
    const response = await apiService.put(`/sales/origens/${id}`, data);
    return response.origem;
  }

  async deleteOrigem(id: number): Promise<void> {
    await apiService.delete(`/sales/origens/${id}`);
  }

  // ============================================================================
  // INDICADORES
  // ============================================================================

  async getIndicadores(search?: string): Promise<Indicador[]> {
    const params = search ? { search } : {};
    const response = await apiService.get('/sales/indicadores', { params });
    return response.indicadores;
  }

  async createIndicador(data: { nome: string; telefone?: string; email?: string }): Promise<Indicador> {
    const response = await apiService.post('/sales/indicadores', data);
    return response.indicador;
  }

  async updateIndicador(id: number, data: { nome: string; telefone?: string; email?: string }): Promise<Indicador> {
    const response = await apiService.put(`/sales/indicadores/${id}`, data);
    return response.indicador;
  }

  async deleteIndicador(id: number): Promise<void> {
    await apiService.delete(`/sales/indicadores/${id}`);
  }

  // ============================================================================
  // PRODUTOS
  // ============================================================================

  async getProdutos(): Promise<Produto[]> {
    const response = await apiService.get('/sales/produtos');
    return response.produtos;
  }

  async createProduto(data: { nome: string; descricao?: string }): Promise<Produto> {
    const response = await apiService.post('/sales/produtos', data);
    return response.produto;
  }

  // ============================================================================
  // PROPOSTAS
  // ============================================================================

  async getPropostas(filters?: { lead_id?: number }): Promise<Proposta[]> {
    const response = await apiService.get('/sales/propostas', { params: filters });
    return response.propostas;
  }

  async createProposta(data: {
    lead_id: number;
    produtos: Array<{ produto_id: number; quantidade?: number }>;
    valor_mensalidade: number;
    valor_start: number;
    dias_validade?: number;
  }): Promise<Proposta> {
    const response = await apiService.post('/sales/propostas', data);
    return response.proposta;
  }

  async enviarProposta(id: number): Promise<{ webview_url: string }> {
    const response = await apiService.post(`/sales/propostas/${id}/enviar`);
    return response;
  }

  // ============================================================================
  // FLOW AUTOMATIONS
  // ============================================================================

  async getFlows(etapa?: string): Promise<FlowConfig[]> {
    const params = etapa ? { etapa } : {};
    const response = await apiService.get('/sales/flows', { params });
    return response.flows.map((flow: any) => ({
      id: flow.id,
      etapa: flow.etapa,
      nome: flow.nome,
      descricao: flow.descricao,
      ativo: flow.is_active,
      nodes: flow.flow_config?.nodes || [],
      edges: flow.flow_config?.edges || [],
      created_at: flow.created_at,
      updated_at: flow.updated_at
    }));
  }

  async getFlow(id: number): Promise<FlowConfig> {
    const response = await apiService.get(`/sales/flows/${id}`);
    const flow = response.flow;
    return {
      id: flow.id,
      etapa: flow.etapa,
      nome: flow.nome,
      descricao: flow.descricao,
      ativo: flow.is_active,
      nodes: flow.flow_config?.nodes || [],
      edges: flow.flow_config?.edges || [],
      created_at: flow.created_at,
      updated_at: flow.updated_at
    };
  }

  async saveFlow(flow: FlowConfig): Promise<FlowConfig> {
    const payload = {
      etapa: flow.etapa,
      nome: flow.nome,
      descricao: flow.descricao,
      ativo: flow.ativo,
      config_json: {
        nodes: flow.nodes,
        edges: flow.edges
      }
    };

    const response = flow.id
      ? await apiService.put(`/sales/flows/${flow.id}`, payload)
      : await apiService.post('/sales/flows', payload);

    return response.flow;
  }

  async updateFlowStatus(id: number, ativo: boolean): Promise<FlowConfig> {
    const response = await apiService.put(`/sales/flows/${id}`, { ativo });
    return response.flow;
  }

  async deleteFlow(id: number): Promise<void> {
    await apiService.delete(`/sales/flows/${id}`);
  }
}

export const salesService = new SalesService();


